<template>
    <article>
        <p>
            In one of my previous posts, we <a href="/blog/chaos-testing-zalando-postgres-operator" target="_blank">looked</a>
            at how a high-availability Postgres cluster managed by the Zalando Postgres Operator
            handles various failure scenarios.
            Now, let's repeat similar experiments against a cluster managed by <a href="https://stackgres.io/" target="_blank">StackGres</a>.
        </p>

        <h2>A brief introduction to StackGres</h2>

        <p>
            StackGres is an open-source Kubernetes Postgres Operator that not only enables you to create a production-ready Postgres
            cluster on any Kubernetes cluster but also automates "Day 2" operations, including high availability, backups, and minor/major Postgres upgrades.
        </p>

        <p>
            At first glance, many Postgres Operators may appear to be similar, offering a common set of features like
            high availability with Patroni and connection pooling with PgBouncer.
            However, in my opinion, each operator reflects the best practices and approaches of the team that created it.
        </p>

        <p>
            For instance, chatting with the Zalando folks about their operator, they told me that their primary goal was to enable development teams to
            autonomously create and manage small databases without requiring the assistance of a DBA.
        </p>

        <p>
            I don't know the story behind StackGres, but it seems to me like its developers aim to offer a user experience similar to that of AWS RDS.
            For example, StackGres offers a user-friendly UI enabling you to create your first cluster in just minutes.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/stackgres/ui.png" />
        </v-card>

        <p>
            Now, that we've got a general idea of StackGres, let's do some chaos testing.
        </p>


        <h2>Setting up the environment</h2>

        <p>
            My lab is a k3s cluster with 3 nodes (4xCPU/8Gb).
        </p>

        <p>
            To install the StackGres operator I used the following Helm commands:
        </p>

        <pre v-hl.bash>
helm repo add stackgres-charts https://stackgres.io/downloads/stackgres-k8s/stackgres/helm/
helm repo update
helm install --create-namespace --namespace stackgres stackgres-operator stackgres-charts/stackgres-operator
        </pre>

        <p>
            The Postgres cluster spec:
        </p>

        <pre v-hl.yaml>
kind: SGInstanceProfile
metadata:
  namespace: default
  name: size-small
spec:
  cpu: "2"
  memory: "4Gi"

---
apiVersion: stackgres.io/v1
kind: SGCluster
metadata:
  namespace: default
  name: db-main
spec:
  postgres:
    version: '15'
  sgInstanceProfile: 'size-small'
  instances: 3
  metadata:
    annotations:
      clusterPods:
        prometheus.io/port: "9090"
        prometheus.io/scrape: "true"
  pods:
    persistentVolume:
      size: '10Gi'
    customContainers:
      - name: coroot-pg-agent
        image: ghcr.io/coroot/coroot-pg-agent:1.2.4
        volumeMounts:
          - mountPath: /var/run/postgresql
            name: socket
        env:
          - name: LISTEN
            value: ":9090"
          - name: DSN
            value: "postgresql://postgres@:5432/postgres?host=/var/run/postgresql&sslmode=disable"
        </pre>

        <p>The cluster will be created once the manifest is applied:</p>

        <pre v-hl.bash>
kubectl apply -f pg.yaml
        </pre>


        <p>
            While StackGres offers built-in monitoring with Prometheus and Grafana, I believe it's crucial to have a
            comprehensive view that includes metrics from both the database and the applications utilizing it.
            As you might have guessed, I'll be using Coroot for this purpose 😉
        </p>

        <pre v-hl.bash>
helm repo add coroot https://coroot.github.io/helm-charts
helm repo update
helm install --namespace coroot --create-namespace coroot coroot/coroot
        </pre>

        <p>
            Now, let's take a look at the Postgres cluster:
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/stackgres/lab.png" />
        </v-card>

        <p>
            Visualizing distributed systems is incredibly useful for comprehending a system's design.
            Coroot leverages eBPF to capture interactions between services, eliminating the need for additional
            instrumentation while ensuring complete coverage without blind spots.
            Furthermore, it can analyze a wide range of application layer protocols, including Postgres, to provide insights into query statuses and latency.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/stackgres/lab_rps.png" />
        </v-card>

        <p>
            Since I've already configured StackGres to run <a href="https://github.com/coroot/coroot-pg-agent" target="_blank">coroot-pg-agent</a>
            as a custom sidecar container, Coroot is now collecting comprehensive Postgres metrics from every instance in the cluster.
        </p>

        <p>
            The final component is <a href="https://chaos-mesh.org/" target="_blank">Chaos Mesh </a> which
            makes it easy to reproduce various failure scenarios.
        </p>

        <pre v-hl.bash>
helm repo add chaos-mesh https://charts.chaos-mesh.org
helm repo update
helm install chaos-mesh chaos-mesh/chaos-mesh \
    -n=chaos-mesh --create-namespace \
    --set chaosDaemon.runtime=containerd \
    --set chaosDaemon.socketPath=/run/k3s/containerd/containerd.sock \
    --version 2.6.2
        </pre>

        <p>
            Ok, we're all set. Let's introduce some chaos 🔥
        </p>

        <h2>A replica failure</h2>

        <p>
            My demo app uses both the primary Postgres instance and its replicas. Let's see what happens if one of the replicas goes down.
        </p>

        <p>
            We could simply delete the Kubernetes pod containing the replica, but the system would quickly detect this situation.
            To make things more challenging, I'll isolate the replica from the network.
        </p>

        <div class="pa-5 my-6">
            <v-img eager max-width="900px" class="ma-auto" src="/static/img/blog/stackgres/replica_failure.png" />
        </div>

        <pre v-hl.yaml>
kind: NetworkChaos
apiVersion: chaos-mesh.org/v1alpha1
metadata:
  name: replica-net-partition
spec:
  selector:
    namespaces: [default]
    labelSelectors:
      stackgres.io/cluster-name: db-main
      role: replica
  mode: one
  action: partition
  direction: both
        </pre>

        <p>
            First, let's examine this failure from the application's perspective.
            There were approximately 30 errors caused by unsuccessful TCP connections between the application and the affected Postgres replica.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/stackgres/replica_failure_app.png" />
        </v-card>

        <p>
            In summary, this failure scenario was promptly and automatically resolved:
        </p>

        <ul>
            <li>
                Patroni detected the disconnection from the API server.
            </li>
            <li>
                Kubernetes removed the affected instance from the available endpoints due to failed readiness probes,
                and app traffic was subsequently rebalanced across the remaining live replica and the primary
            </li>
        </ul>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/stackgres/replica_failure1.png" />
        </v-card>

        <h2>A failure of the primary instance</h2>

        <p>
            Clearly, if the primary Postgres instance fails, it will impact its clients, as write queries cannot be
            redistributed among the read-only replicas.
        </p>

        <div class="pa-5 my-6">
            <v-img eager max-width="900px" class="ma-auto" src="/static/img/blog/stackgres/primary_failure.png" />
        </div>

        <pre v-hl.yaml>
kind: NetworkChaos
apiVersion: chaos-mesh.org/v1alpha1
metadata:
  name: replica-net-partition
spec:
  selector:
    namespaces: [default]
    labelSelectors:
      stackgres.io/cluster-name: db-main
      role: master
  mode: one
  action: partition
  direction: both
        </pre>

        <p>
            Handling a primary server failure is a complex process because, in addition to switchover, it involves tasks such
            as reconfiguring replication and running <var>VACUUM ANALYZE</var> on the new primary.
            These tasks can degrade database performance due to increased CPU and I/O usage, as well as the need for cache warm-up.
        </p>

        <p>
            To provide you with an overview of the system's behavior during this failure,
            I'll use Coroot's <a href="/blog/explaining-application-performance-anomalies-with-ai" target="_blank">Automated RCA (Root Cause Analysis)</a> feature.
            It's designed to pinpoint all the relevant events within the cluster that might have affected the application.
            Therefore, I've selected the anomalies in the app's latency and errors and requested Coroot to explain them for me:
        </p>

        <div class="pa-5 my-6">
            <v-img eager class="ma-auto" src="/static/img/blog/stackgres/primary_failure_rca.png" />
        </div>

        <p>
            Let's analyze this report:
        </p>

        <ul>
            <li>The network connectivity issue was identified through an increase in TCP retransmissions.</li>
            <li>A switchover occurred in the DB cluster, leading to application errors.</li>
            <li>Following the switchover, we observed high I/O latency on the Postgres data volume, which in turn caused elevated query latency.</li>
        </ul>

        <p>In total, this incident resulted in <var>~2500</var> app errors within 3 minutes.</p>

        <h2>Conclusion</h2>

        <p>
            After our experiments, we can confidently say that StackGres and its underlying components effectively
            handle failures in the cluster.
        </p>

        <p>
            As for my experience with StackGres, it was pleasantly surprising. What I liked most was its user interface
            and how the operator enables you to schedule and monitor Database Operations, including minor/major upgrades, vacuuming, and repacking.
        </p>

        <p>
            When it comes to monitoring, it's great that StackGres offers built-in Postgres monitoring capabilities.
            However, in shared infrastructures like Kubernetes, it's essential to have a broader view &mdash; seeing all the applications,
            understanding their interactions, and monitoring shared compute resource usage.
        </p>

        <p>
            Coroot can help you achieve this by providing comprehensive observability across the entire cluster.
            StackGres is supported out-of-the-box in both Coroot Cloud and Coroot Community Edition.
        </p>

        <p>
            Try <router-link :to="{name: 'cloud'}">Coroot Cloud</router-link> now (14-day free trial is available) or follow the instructions on our <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Getting started</router-link> page to install Coroot Community Edition.
        </p>

        <p>
            If you like Coroot, give us a ⭐ on <a href="https://github.com/coroot/coroot" target="_blank">GitHub</a>️.
        </p>

        <p>Any questions or feedback? Reach out to us on <a href="https://join.slack.com/t/coroot-community/shared_invite/zt-1gsnfo0wj-I~Zvtx5CAAb8vr~r~vecyw" target="_blank"><v-icon>mdi-slack</v-icon>Slack</a>.</p>
    </article>
</template>

<script>


export default {
    components: {},
}
</script>

<style scoped>
</style>
